import Divider from '@mui/material/Divider';
import ListSubheader from '@mui/material/ListSubheader';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import * as React from 'react';
import { settingsRoutes } from '../../../../../routes';
import { useClientFields } from '../../../../../sic-lib/api/hooks/useClientFields';
import { CONTACTS_FIELD_EMPTY_ROWID, SYSTEM_PROP_ID } from '../../../../../sic-lib/defaults';
import { Contact } from '../../../../../sic-lib/types/Contact';
import { ContactField } from '../../../../../sic-lib/types/ContactField';
import { hasDistinctFieldId } from '../../../../../sic-lib/utils/functionalFilters';
import {
    mapClientFieldToContactField,
    mapToListOfFieldIds,
} from '../../../../../sic-lib/utils/functionalMappers';
import { groupClientFieldsByValidationType } from '../../../../../sic-lib/utils/functionalReducers';
import NavLinkMenuItem from '../../../../Navigation/NavLinkMenuItem/NavLinkMenuItem';

interface Props {
    contact: Contact;
    anchorEl: HTMLElement | null;
    menuId: string;
    contactFields: ContactField[];
    onSelectHandler: React.Dispatch<{
        unsavedField: ContactField;
    }>;
    onCloseHandler: () => void;
}

export default function MenuAddContactFieldRaw({
    contact,
    anchorEl,
    menuId,
    contactFields,
    onSelectHandler,
    onCloseHandler,
}: Props) {
    const { data: clientFields } = useClientFields();

    const groupedFields = React.useMemo(() => {
        return groupClientFieldsByValidationType(
            (clientFields || []).filter(
                hasDistinctFieldId(contact.values.map(mapToListOfFieldIds)),
            ),
        );
    }, [clientFields, contact.values]);

    const handleSelect = (event: React.MouseEvent<HTMLElement>) => {
        const { fieldid } = event.currentTarget.dataset;
        if (!fieldid) return;

        const clientField = (clientFields || []).find(field => field.fieldId === +fieldid);
        if (!clientField) return;

        const mappedField = mapClientFieldToContactField({
            leadId: contact.contactId,
            rowId: CONTACTS_FIELD_EMPTY_ROWID,
        })(clientField);

        onSelectHandler({
            unsavedField: mappedField,
        });

        onCloseHandler();
    };

    return (
        <Menu id={menuId} anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={onCloseHandler}>
            {groupedFields.phone.length > 0 && (
                <div>
                    <ListSubheader>Phone</ListSubheader>
                    {groupedFields.phone.map(fld => (
                        <MenuItem
                            key={fld.fieldId}
                            data-fieldid={fld.fieldId}
                            onClick={handleSelect}
                        >
                            {fld.label}
                        </MenuItem>
                    ))}
                    <Divider />
                </div>
            )}
            {groupedFields.email.length > 0 && (
                <div>
                    <ListSubheader>Email</ListSubheader>
                    {groupedFields.email.map(fld => (
                        <MenuItem
                            key={fld.fieldId}
                            data-group="email"
                            data-fieldid={fld.fieldId}
                            onClick={handleSelect}
                        >
                            {fld.label}
                        </MenuItem>
                    ))}
                    <Divider />
                </div>
            )}
            {groupedFields.address.length > 0 && (
                <div>
                    <ListSubheader>Address</ListSubheader>
                    {groupedFields.address.map(fld => (
                        <MenuItem
                            key={fld.fieldId}
                            data-group="address"
                            data-fieldid={fld.fieldId}
                            onClick={handleSelect}
                        >
                            {fld.label}
                        </MenuItem>
                    ))}
                    <Divider />
                </div>
            )}
            {groupedFields.date.length > 0 && (
                <div>
                    <ListSubheader>Dates</ListSubheader>
                    {groupedFields.date.map(fld => (
                        <MenuItem
                            key={fld.fieldId}
                            data-group="date"
                            data-fieldid={fld.fieldId}
                            onClick={handleSelect}
                        >
                            {fld.label}
                        </MenuItem>
                    ))}
                    <Divider />
                </div>
            )}
            {groupedFields.dollar.length > 0 && (
                <div>
                    <ListSubheader>Monetary</ListSubheader>
                    {groupedFields.dollar.map(fld => (
                        <MenuItem
                            key={fld.fieldId}
                            data-group="dollar"
                            data-fieldid={fld.fieldId}
                            onClick={handleSelect}
                        >
                            {fld.label}
                        </MenuItem>
                    ))}
                    <Divider />
                </div>
            )}
            {groupedFields.year.length > 0 && (
                <div>
                    <ListSubheader>Year</ListSubheader>
                    {groupedFields.year.map(fld => (
                        <MenuItem
                            key={fld.fieldId}
                            data-group="year"
                            data-fieldid={fld.fieldId}
                            onClick={handleSelect}
                        >
                            {fld.label}
                        </MenuItem>
                    ))}
                    <Divider />
                </div>
            )}
            {groupedFields.other.length > 0 && (
                <div>
                    <ListSubheader>Other</ListSubheader>
                    {groupedFields.other.map(
                        fld =>
                            (fld.fieldId === 3 || fld.fieldId > SYSTEM_PROP_ID) && (
                                <MenuItem
                                    key={fld.fieldId}
                                    data-group="other"
                                    data-fieldid={fld.fieldId}
                                    onClick={handleSelect}
                                >
                                    {fld.label}
                                </MenuItem>
                            ),
                    )}
                    <Divider />
                </div>
            )}
            <NavLinkMenuItem pathname={settingsRoutes.customFields}>Manage fields</NavLinkMenuItem>
        </Menu>
    );
}
